import { DEFAULT_CHART_CONFIGS, DEFAULT_COLORS } from './chart-defaults';
import json5 from "json5";

/**
 * Deep merge utility function for merging chart configurations
 * Handles nested objects while preserving arrays as-is
 * 
 * @param target - The target object to merge into
 * @param source - The source object to merge from
 * @returns The merged object
 */
export function deepMerge(target: any, source: any): any {
  if (source === null || source === undefined) {
    return target;
  }
  
  if (typeof source !== 'object' || typeof target !== 'object') {
    return source;
  }
  
  if (Array.isArray(source)) {
    return source;
  }
  
  const merged = { ...target };
  
  for (const key in source) {
    if (source.hasOwnProperty(key)) {
      if (typeof source[key] === 'object' && source[key] !== null && !Array.isArray(source[key])) {
        merged[key] = deepMerge(merged[key] || {}, source[key]);
      } else {
        merged[key] = source[key];
      }
    }
  }
  
  return merged;
}

/**
 * Apply defaults to chart configuration
 * Transforms minimal backend data into a full Chart.js configuration with defaults
 * 
 * @param config - The minimal chart configuration from backend
 * @returns Enhanced chart configuration with applied defaults
 */
export function applyChartDefaults(config: any): any {
  const { type, data, options = {} } = config;
  
  // Get default configuration for chart type
  const defaults = DEFAULT_CHART_CONFIGS[type as keyof typeof DEFAULT_CHART_CONFIGS];
  if (!defaults) {
    return config;
  }
  
  // Apply dataset defaults and colors
  const enhancedData = { ...data };
  if (enhancedData.datasets) {
    enhancedData.datasets = enhancedData.datasets.map((dataset: any, index: number) => {
      let datasetWithDefaults = {
        ...defaults.datasetDefaults,
        ...dataset
      };
      
      // Handle colors differently for pie/doughnut charts vs line/bar charts
      if (type === 'pie' || type === 'doughnut' || type === 'polarArea') {
        // For pie charts, apply different colors to each data point
        const numDataPoints = dataset.data?.length || 0;
        const backgroundColors = [];
        const borderColors = [];
        
        for (let i = 0; i < numDataPoints; i++) {
          const colorIndex = i % DEFAULT_COLORS.length;
          const color = DEFAULT_COLORS[colorIndex];
          backgroundColors.push(color.backgroundColor);
          borderColors.push(color.borderColor);
        }
        
        datasetWithDefaults.backgroundColor = backgroundColors;
        datasetWithDefaults.borderColor = borderColors;
      } else {
        // For line/bar charts, apply one color per dataset
        const colorIndex = index % DEFAULT_COLORS.length;
        const defaultColor = DEFAULT_COLORS[colorIndex];
        datasetWithDefaults = {
          ...datasetWithDefaults,
          ...defaultColor
        };
      }
      
      return datasetWithDefaults;
    });
  }
  
  // Handle title configuration - move from top level to plugins if needed
  let processedOptions = { ...options };
  if (options.title && !options.plugins?.title) {
    processedOptions = {
      ...options,
      plugins: {
        ...options.plugins,
        title: options.title
      }
    };
    delete processedOptions.title;
  }
  
  // Merge options with defaults
  const enhancedOptions = deepMerge(defaults.options, processedOptions);
  
  // Debug logging
  console.log('🔍 Chart Defaults Debug:');
  console.log('📊 Chart Type:', type);
  console.log('⚙️ Default Options:', JSON.stringify(defaults.options, null, 2));
  console.log('📥 Backend Options:', JSON.stringify(options, null, 2));
  console.log('🔧 Processed Options:', JSON.stringify(processedOptions, null, 2));
  console.log('🔄 Enhanced Options:', JSON.stringify(enhancedOptions, null, 2));
  
  return {
    ...config,
    data: enhancedData,
    options: enhancedOptions
  };
}

/**
 * Converts chart text content to formatted chartjs markdown
 * @param chartText - The raw chart text content
 * @returns The formatted chartjs markdown string
 */
export function convertChartToMarkdown(chartText: string): string {
  console.log("Processing chart diagram:", chartText);
  
  // Step 1: Remove the "chartjs\n" prefix from the text
  let jsonString = chartText.slice(12, -3);
  console.log("Chart JSON string (after slice):", jsonString);

  // Step 2: Parse the string into a JavaScript object
  let jsonObject = json5.parse(jsonString);
  console.log("Parsed chart JSON object:", jsonObject);

  // Step 3: Re-format the object into a readable string
  let formattedJsonString = JSON.stringify(
    jsonObject,
    null,
    2
  );
  console.log(
    "Formatted chart JSON string:",
    formattedJsonString
  );

  // Add the prefix back to the final formatted string
  let finalResult = "\n\n```chartjs\n" + formattedJsonString + "\n```";
  console.log("Final chartjs markdown string:", finalResult);

  return finalResult;
}

